

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  Card,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import Icon from "@mui/material/Icon";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Loader2 } from 'lucide-react';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import colors from "assets/theme/base/colors";
import EmailModal from 'components/EmailModal/index.js';
import InvoiceViewer from './InvoiceViewer';
import { Snackbar, Alert } from '@mui/material';
import { formatDate } from 'utils/DateTimeConversion';
import { serializeEmailAttachments } from 'utils/emailAttachments';
import EmptyState from 'components/EmptyTable/EmptyState';
import { usePermission } from 'auth/usePermission';
import { useTravelUi } from '../../hooks/useTravelUi';
import { frontendLogger } from '../../utils/logger';
import useOpenCreateFromUrl from "hooks/useOpenCreateFromUrl";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";
import { useActiveApp } from "shell-v2/apps";


function Invoices() {
  const { axiosInstance, user } = useAuth();
  // UI v2 layout (design-reference "Travel · Bookings list"): page header,
  // then the table in one card.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { app } = useActiveApp();
  const invoicesLabel = app?.labels?.invoices || 'Invoices';
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [currentInvoiceHtml, setCurrentInvoiceHtml] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const { canCreate } = usePermission('invoices');
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  // New state for edit invoice modal
  const [openEditInvoiceModal, setOpenEditInvoiceModal] = useState(false);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [editedInvoiceItems, setEditedInvoiceItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [productList, setProductList] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewLoadingId, setViewLoadingId] = useState(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  // Add these with other state declarations
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [editLoadingId, setEditLoadingId] = useState(null);
  const [openViewInvoiceModal, setOpenViewInvoiceModal] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewInvoiceItems, setViewInvoiceItems] = useState([]);
  // Add these state declarations at the top with other states
  // Add near the top with other state declarations
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState({ to: '', cc: '', bcc: '', subject: '', content: '' });
  const [emailContext, setEmailContext] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const { showTravel, loading: travelUiLoading } = useTravelUi();
  const [travelOrders, setTravelOrders] = useState([]);
  const [openCreateInvoiceModal, setOpenCreateInvoiceModal] = useState(false);
  const [isCreatingManualInvoice, setIsCreatingManualInvoice] = useState(false);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [invoiceTypeOptions, setInvoiceTypeOptions] = useState([]);
  const [manualInvoiceForm, setManualInvoiceForm] = useState({
    travel_order_id: '',
    invoice_type: '',
    payment_stage: '',
    installment_number: null,
    payment_template_type: '',
    total_amount: '',
    due_date: '',
    notes: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axiosInstance.get('/products/'); // Adjust the endpoint as necessary
        setProductList(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchProducts();
  }, []);

  const fetchTravelOrders = useCallback(async () => {
    try {
      const orderRes = await axiosInstance.get('/travel-orders/');
      const secured = (orderRes.data || []).filter(
        (o) => ['Secured', 'Booking Confirmed'].includes(o.status)
      );
      setTravelOrders(secured);
    } catch (error) {
      console.error('Error fetching travel orders:', error);
      setTravelOrders([]);
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (!showTravel) {
      setTravelOrders([]);
      setOpenCreateInvoiceModal(false);
      setSelectedOrderDetail(null);
      setInvoiceTypeOptions([]);
      return;
    }
    fetchTravelOrders();
  }, [showTravel, fetchTravelOrders]);

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      // Travel on: travel-order + project/RE invoices (legacy sales-order hidden).
      // Travel off: non-travel invoices only.
      // v2 keeps standard Orders beside Travel, so it lists every kind.
      const params = isV2 && showTravel ? {} : { order_kind: showTravel ? 'travel' : 'normal' };
      const response = await axiosInstance.get('/invoices/', { params });
      const invoicesWithCurrency = await Promise.all(
        response.data.map(async (invoice) => {
          if (invoice.account_id) {
            try {
              const accountResponse = await axiosInstance.get(`/accounts/${invoice.account_id}`);
              return {
                ...invoice,
                account_currency: accountResponse.data.currency || 'INR'
              };
            } catch (error) {
              console.error('Error fetching account details:', error);
              return {
                ...invoice,
                account_currency: 'INR'
              };
            }
          }
          return {
            ...invoice,
            account_currency: 'INR'
          };
        })
      );
      setInvoices(invoicesWithCurrency);
      setFilteredInvoices(invoicesWithCurrency);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [axiosInstance, showTravel, isV2]);

  useEffect(() => {
    if (travelUiLoading) return;
    fetchInvoices();
    fetchAccounts();
  }, [travelUiLoading, showTravel, fetchInvoices]);

  const buildInvoiceTypeOptions = (orderDetail, existingInvoices = []) => {
    const schedule = orderDetail?.payment?.schedule || [];
    const paymentTemplate = orderDetail?.payment?.payment_template;

    const existingKeys = new Set(
      existingInvoices.map((inv) => {
        if (inv.invoice_type === 'installment' && inv.installment_number != null) {
          return `installment-${inv.installment_number}`;
        }
        return inv.invoice_type;
      })
    );

    const mapScheduleRow = (row) => {
      const label = row.label || '';
      let invoiceType = 'full';
      let installmentNumber = null;

      if (label.startsWith('Installment ')) {
        invoiceType = 'installment';
        installmentNumber = parseInt(label.replace('Installment ', ''), 10);
      } else if (label === 'Deposit') {
        invoiceType = 'deposit';
      } else if (label === 'Final Balance') {
        invoiceType = 'final';
      } else if (label === 'Full Payment') {
        invoiceType = 'full';
      }

      const key = invoiceType === 'installment' ? `installment-${installmentNumber}` : invoiceType;
      return {
        key,
        label,
        invoice_type: invoiceType,
        payment_stage: label,
        installment_number: installmentNumber,
        amount: row.amount,
        due_date: row.due_date ? String(row.due_date).slice(0, 10) : '',
        payment_template_type: paymentTemplate?.payment_type || '',
      };
    };

    if (schedule.length > 0) {
      return schedule
        .map(mapScheduleRow)
        .filter((opt) => opt.key && !existingKeys.has(opt.key));
    }

    if (!paymentTemplate?.payment_type) {
      return existingKeys.has('full')
        ? []
        : [{
          key: 'full',
          label: 'Full Payment',
          invoice_type: 'full',
          payment_stage: 'Full Payment',
          installment_number: null,
          amount: orderDetail?.payment?.total_amount || '',
          due_date: '',
          payment_template_type: 'full_payment',
        }];
    }

    const fallback = [];
    const total = orderDetail?.payment?.total_amount;
    const deposit = orderDetail?.payment?.paid_amount;
    const pending = orderDetail?.payment?.pending_amount;

    if (paymentTemplate.payment_type === 'full_payment' && !existingKeys.has('full')) {
      fallback.push({
        key: 'full',
        label: 'Full Payment',
        invoice_type: 'full',
        payment_stage: 'Full Payment',
        installment_number: null,
        amount: total,
        due_date: '',
        payment_template_type: paymentTemplate.payment_type,
      });
    }
    if (paymentTemplate.payment_type === 'deposit_final_balance') {
      if (!existingKeys.has('deposit')) {
        fallback.push({
          key: 'deposit',
          label: 'Deposit',
          invoice_type: 'deposit',
          payment_stage: 'Deposit',
          installment_number: null,
          amount: deposit,
          due_date: '',
          payment_template_type: paymentTemplate.payment_type,
        });
      }
      if (!existingKeys.has('final')) {
        fallback.push({
          key: 'final',
          label: 'Final Balance',
          invoice_type: 'final',
          payment_stage: 'Final Balance',
          installment_number: null,
          amount: pending,
          due_date: '',
          payment_template_type: paymentTemplate.payment_type,
        });
      }
    }
    if (paymentTemplate.payment_type === 'deposit_installments') {
      if (!existingKeys.has('deposit')) {
        fallback.push({
          key: 'deposit',
          label: 'Deposit',
          invoice_type: 'deposit',
          payment_stage: 'Deposit',
          installment_number: null,
          amount: deposit,
          due_date: '',
          payment_template_type: paymentTemplate.payment_type,
        });
      }
      (paymentTemplate.installments || []).forEach((inst) => {
        const num = inst.installment_number;
        const key = `installment-${num}`;
        if (!existingKeys.has(key)) {
          fallback.push({
            key,
            label: `Installment ${num}`,
            invoice_type: 'installment',
            payment_stage: `Installment ${num}`,
            installment_number: num,
            amount: '',
            due_date: '',
            payment_template_type: paymentTemplate.payment_type,
          });
        }
      });
    }
    return fallback;
  };

  const handleTravelOrderSelect = async (orderId) => {
    setManualInvoiceForm((prev) => ({
      ...prev,
      travel_order_id: orderId || '',
      invoice_type: '',
      payment_stage: '',
      installment_number: null,
      payment_template_type: '',
      total_amount: '',
      due_date: '',
    }));
    setSelectedOrderDetail(null);
    setInvoiceTypeOptions([]);

    if (!orderId) return;

    setLoadingOrderDetail(true);
    try {
      const [detailRes] = await Promise.all([
        axiosInstance.get(`/travel-orders/${orderId}/detail`),
      ]);
      const orderDetail = detailRes.data;
      const existingForOrder = invoices.filter(
        (inv) => String(inv.travel_order_id) === String(orderId)
      );
      const options = buildInvoiceTypeOptions(orderDetail, existingForOrder);
      setSelectedOrderDetail(orderDetail);
      setInvoiceTypeOptions(options);

      if (options.length === 1) {
        const opt = options[0];
        setManualInvoiceForm((prev) => ({
          ...prev,
          invoice_type: opt.invoice_type,
          payment_stage: opt.payment_stage,
          installment_number: opt.installment_number,
          payment_template_type: opt.payment_template_type,
          total_amount: opt.amount != null && opt.amount !== '' ? String(opt.amount) : '',
          due_date: opt.due_date || '',
        }));
      }
    } catch (error) {
      console.error('Error loading travel order detail:', error);
      enqueueSnackbar('Failed to load payment terms for this order', { variant: 'error' });
    } finally {
      setLoadingOrderDetail(false);
    }
  };

  const handleInvoiceTypeSelect = (option) => {
    if (!option) {
      setManualInvoiceForm((prev) => ({
        ...prev,
        invoice_type: '',
        payment_stage: '',
        installment_number: null,
        payment_template_type: '',
        total_amount: '',
        due_date: '',
      }));
      return;
    }
    setManualInvoiceForm((prev) => ({
      ...prev,
      invoice_type: option.invoice_type,
      payment_stage: option.payment_stage,
      installment_number: option.installment_number,
      payment_template_type: option.payment_template_type,
      total_amount: option.amount != null && option.amount !== '' ? String(option.amount) : prev.total_amount,
      due_date: option.due_date || prev.due_date,
    }));
  };

  const handleOpenCreateInvoiceModal = () => {
    setManualInvoiceForm({
      travel_order_id: '',
      invoice_type: '',
      payment_stage: '',
      installment_number: null,
      payment_template_type: '',
      total_amount: '',
      due_date: '',
      notes: '',
    });
    setSelectedOrderDetail(null);
    setInvoiceTypeOptions([]);
    setOpenCreateInvoiceModal(true);
  };
  // v2 header "New invoice": the same dialog and the same gate as Create Invoice.
  useOpenCreateFromUrl(handleOpenCreateInvoiceModal, {
    ready: !isLoading && !travelUiLoading,
    allowed: showTravel && canCreate,
  });

  const handleCreateManualInvoice = async () => {
    try {
      if (!manualInvoiceForm.travel_order_id || !manualInvoiceForm.invoice_type || !manualInvoiceForm.total_amount) {
        enqueueSnackbar('Travel order, invoice type, and amount are required', { variant: 'warning' });
        return;
      }
      setIsCreatingManualInvoice(true);
      const selectedOrder = travelOrders.find((o) => String(o.id) === String(manualInvoiceForm.travel_order_id));
      const isPaidType = manualInvoiceForm.invoice_type === 'full' || manualInvoiceForm.invoice_type === 'deposit';
      const payload = {
        travel_order_id: Number(manualInvoiceForm.travel_order_id),
        invoice_type: manualInvoiceForm.invoice_type,
        payment_stage: manualInvoiceForm.payment_stage || null,
        installment_number: manualInvoiceForm.installment_number,
        payment_template_type: manualInvoiceForm.payment_template_type || null,
        total_amount: parseFloat(manualInvoiceForm.total_amount),
        subtotal: parseFloat(manualInvoiceForm.total_amount),
        total_order_amount: selectedOrderDetail?.payment?.total_amount || null,
        balance_amount: selectedOrderDetail?.payment?.pending_amount || null,
        trip_name: selectedOrderDetail?.trip?.title || null,
        departure_date: selectedOrderDetail?.departure_date || null,
        client_name: selectedOrderDetail?.customer_name || null,
        payment_status: isPaidType ? 'Paid' : 'Pending',
        status: isPaidType ? 'Paid' : 'Issued',
        order_number: selectedOrder?.name || selectedOrderDetail?.order_id || '',
        notes: manualInvoiceForm.notes || '',
        due_date: manualInvoiceForm.due_date || null,
        invoice_date: new Date().toISOString().split('T')[0],
        currency: 'GBP',
      };
      await axiosInstance.post('/invoices/', payload);
      enqueueSnackbar('Travel invoice created successfully', { variant: 'success' });
      setOpenCreateInvoiceModal(false);
      setManualInvoiceForm({
        travel_order_id: '',
        invoice_type: '',
        payment_stage: '',
        installment_number: null,
        payment_template_type: '',
        total_amount: '',
        due_date: '',
        notes: '',
      });
      setSelectedOrderDetail(null);
      setInvoiceTypeOptions([]);
      fetchInvoices();
    } catch (error) {
      console.error('Error creating manual travel invoice:', error);
      enqueueSnackbar('Failed to create manual travel invoice', { variant: 'error' });
    } finally {
      setIsCreatingManualInvoice(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/accounts/');
      const formattedAccounts = response.data.map(account => ({
        value: account.id,
        label: account.name || `Account ${account.id}`,
        fullData: account,
        currency: account.currency
      }));
      setAccounts(formattedAccounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };
  useEffect(() => {
    if (openEditInvoiceModal) {
      fetchAccounts();
    }
  }, [openEditInvoiceModal]);

  const handleEditInvoice = async (invoiceId) => {
    try {
      setIsEditLoading(true);
      setEditLoadingId(invoiceId);

      // Fetch full invoice details
      const invoiceResponse = await axiosInstance.get(`/invoices/${invoiceId}/`);

      // Fetch order details if sales_order_id exists
      let orderCurrency = 'INR'; // Default currency
      if (invoiceResponse.data.sales_order_id) {
        try {
          const orderResponse = await axiosInstance.get(`/orders/${invoiceResponse.data.sales_order_id}`);
          if (orderResponse.data.account_id) {
            const accountResponse = await axiosInstance.get(`/accounts/${orderResponse.data.account_id}`);
            orderCurrency = accountResponse.data.currency || 'INR';
          }
        } catch (error) {
          console.error('Error fetching order details:', error);
        }
      }

      // Fetch invoice items
      const invoiceItemsResponse = await axiosInstance.get(`/invoices/${invoiceId}/items/`);

      setEditedInvoice({
        ...invoiceResponse.data,
        account_currency: orderCurrency,
        invoice_date: invoiceResponse.data.invoice_date
          ? new Date(invoiceResponse.data.invoice_date).toISOString().split('T')[0]
          : null,
        due_date: invoiceResponse.data.due_date
          ? new Date(invoiceResponse.data.due_date).toISOString().split('T')[0]
          : null,
      });

      setEditedInvoiceItems(
        invoiceItemsResponse.data.map(item => ({
          ...item,
          markedForDeletion: false,
          tempId: item.id ? item.id : Date.now() + Math.random()
        }))
      );

      setOpenEditInvoiceModal(true);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      enqueueSnackbar("Failed to load invoice details", { variant: "error" });
    } finally {
      setIsEditLoading(false);
      setEditLoadingId(null);
    }
  };

  const handleInvoiceFieldChange = (field, value) => {
    setEditedInvoice(prev => {
      const updates = { ...prev, [field]: value };

      // If changing account, update related fields
      if (field === 'account_id') {
        const selectedAccount = accounts.find(acc => acc.value === value);
        if (selectedAccount) {
          updates.account_name = selectedAccount.label;
          updates.account_currency = selectedAccount.currency;
        }
      }

      return updates;
    });
  };

  const handleInvoiceItemChange = (tempId, field, value) => {
    const updatedItems = [...editedInvoiceItems];
    const itemIndex = updatedItems.findIndex(item => item.tempId === tempId);
    if (itemIndex === -1) return;

    const item = updatedItems[itemIndex];
    if (field === "discount_percentage") {
      if (value > 100) value = 100;
      else if (value < 0) value = 0;
    }

    if (field === 'product_name') {
      const selectedProduct = productList.find(p => p.name === value);
      if (selectedProduct) {
        item.unit_price = selectedProduct.price;
        item.tax = selectedProduct.tax_rate;
        item.product_id = selectedProduct.id;
        item.discounted_price = item.unit_price - (item.unit_price * item.discount_percentage / 100);
      }
    }

    item[field] = field === 'product_name' ? value : (value === '' ? 0 : parseFloat(value));
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discountPercentage = parseFloat(item.discount_percentage) || 0;
    const taxRate = parseFloat(item.tax) || 0;
    const discountedPrice = unitPrice * (1 - discountPercentage / 100);
    const itemNet = discountedPrice * quantity;
    item.discounted_price = discountedPrice;
    item.total_price = itemNet * (1 + taxRate / 100);

    setEditedInvoiceItems(updatedItems);
  };

  const handleAddInvoiceItem = () => {
    const newItem = {
      product_name: '',
      unit_price: 0,
      quantity: 1,
      discount_percentage: 0,
      tax: 0,
      total_price: 0,
      discounted_price: 0,
      markedForDeletion: false,
      tempId: Date.now() + Math.random() // Unique temporary ID
    };
    setEditedInvoiceItems(prev => [...prev, newItem]);
  };

  const handleRemoveInvoiceItem = (tempId) => {
    const activeItems = editedInvoiceItems.filter(item => !item.markedForDeletion);
    if (activeItems.length <= 1) {
      enqueueSnackbar('At least one item is required in the invoice', { variant: 'warning' });
      return;
    }

    const updatedItems = [...editedInvoiceItems];
    const itemIndex = updatedItems.findIndex(item => item.tempId === tempId);
    if (itemIndex === -1) return;

    const itemToRemove = updatedItems[itemIndex];
    if (itemToRemove.id) {
      updatedItems[itemIndex] = { ...itemToRemove, markedForDeletion: true };
    } else {
      updatedItems.splice(itemIndex, 1);
    }

    setEditedInvoiceItems(updatedItems);
  };

  const handleSaveInvoice = async () => {
    try {
      setIsLoading(true);

      // Filter out blank rows (rows with no product_name) and marked deletions
      const validItems = editedInvoiceItems.filter(item =>
        !item.markedForDeletion &&
        item.product_name &&
        item.product_name.trim() !== ''
      );

      if (validItems.length === 0) {
        enqueueSnackbar('At least one valid item is required', { variant: 'error' });
        setIsLoading(false);
        return;
      }

      if (editedInvoice?.invoice_date && editedInvoice?.due_date) {
        const invoiceDate = new Date(editedInvoice.invoice_date);
        const dueDate = new Date(editedInvoice.due_date);
        if (dueDate < invoiceDate) {
          enqueueSnackbar('Due date cannot be earlier than invoice date', { variant: 'error' });
          setIsLoading(false);
          return;
        }
      }

      const invalidQtyItem = validItems.find((item) => {
        const qty = parseFloat(item.quantity);
        return Number.isNaN(qty) || qty <= 0;
      });
      if (invalidQtyItem) {
        enqueueSnackbar('Quantity must be greater than 0 for all line items', { variant: 'error' });
        setIsLoading(false);
        return;
      }

      // Calculate tax summary and totals (original calculation logic)
      const { subtotal, grandTotal, taxSummary } = calculateTotalAmount(validItems);

      // Convert tax summary into arrays for tax slabs (original code)
      const taxSlabs = Object.entries(taxSummary).map(([rate, amount]) => ({
        tax_rate: parseFloat(rate),
        tax_amount: parseFloat(amount.toFixed(2))
      }));

      // Prepare invoice payload with tax information (original structure)
      const invoicePayload = {
        id: editedInvoice.id,
        invoice_number: editedInvoice.invoice_number,
        invoice_date: editedInvoice.invoice_date,
        due_date: editedInvoice.due_date,
        status: editedInvoice.status,
        notes: editedInvoice.notes || '',
        total_amount: parseFloat(grandTotal.toFixed(2)), // Properly formatted total amount
        subtotal: parseFloat(subtotal.toFixed(2)), // Add subtotal
        account_id: editedInvoice.account_id,
        account_name: editedInvoice.account_name,
        currency: editedInvoice.currency || editedInvoice.account_currency,
        account_currency: editedInvoice.account_currency,
        // Add tax slab information in the format expected by the server
        tax_slabs: taxSlabs.map((slab, index) => ({
          slab_number: index + 1,
          tax_rate: slab.tax_rate,
          tax_amount: slab.tax_amount
        }))
      };
      console.log('Sending payload:', invoicePayload); // Original debug logging

      // Update invoice (original API call)
      await axiosInstance.put(`/invoices/${editedInvoice.id}/`, invoicePayload);

      // --- Log invoice update ---
      await frontendLogger.logUpdate('Invoice', {
        id: editedInvoice.id,
        action: 'update',
        updated_by: user?.id,
        updated_at: new Date().toISOString(),
      });

      // Delete items marked for deletion (new addition)
      const itemsToDelete = editedInvoiceItems.filter(item => item.id && item.markedForDeletion);
      for (const item of itemsToDelete) {
        await axiosInstance.delete(`/invoices/items/${item.id}/`);
      }

      // Handle invoice items individually (original logic with valid items only)
      for (const item of validItems) {
        const itemPayload = {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_percentage: parseFloat(item.discount_percentage) || 0,
          tax: parseFloat(item.tax) || 0,
          total_price: parseFloat((item.total_price || 0).toFixed(2)),
          discounted_price: parseFloat((item.discounted_price || 0).toFixed(2))
        };

        if (item.id) {
          // Update existing item (original code)
          await axiosInstance.put(`/invoices/items/${item.id}/`, itemPayload);
        } else {
          // Create new item (original code)
          await axiosInstance.post(`/invoices/${editedInvoice.id}/items/`, itemPayload);
        }
      }

      // Refresh invoices (original code)
      await fetchInvoices();
      setOpenEditInvoiceModal(false);
      enqueueSnackbar('Invoice updated successfully', { variant: 'success' });

      // --- Log invoice creation ---
      await frontendLogger.logCreate('Invoice', {
        id: editedInvoice.id,
        action: 'create',
        created_by: user?.id,
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error saving invoice:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      enqueueSnackbar('Failed to save invoice', { variant: 'error' });
    } finally {
      setIsLoading(false);
      // setOpenEditInvoiceModal(false);
    }
  };

  const displayInvoice = async (invoiceId) => {
    try {
      setIsViewLoading(true);
      setViewLoadingId(invoiceId);

      // Fetch full invoice details
      const invoiceResponse = await axiosInstance.get(`/invoices/${invoiceId}/`);

      // Fetch order details if sales_order_id exists
      let orderCurrency = 'INR'; // Default currency
      if (invoiceResponse.data.sales_order_id) {
        try {
          const orderResponse = await axiosInstance.get(`/orders/${invoiceResponse.data.sales_order_id}`);
          if (orderResponse.data.account_id) {
            const accountResponse = await axiosInstance.get(`/accounts/${orderResponse.data.account_id}`);
            orderCurrency = accountResponse.data.currency || 'INR';
          }
        } catch (error) {
          console.error('Error fetching order details:', error);
        }
      }

      // Fetch invoice items
      const invoiceItemsResponse = await axiosInstance.get(`/invoices/${invoiceId}/items/`);

      setViewInvoice({
        ...invoiceResponse.data,
        account_currency: orderCurrency,
        invoice_date: invoiceResponse.data.invoice_date
          ? new Date(invoiceResponse.data.invoice_date).toISOString().split('T')[0]
          : null,
        due_date: invoiceResponse.data.due_date
          ? new Date(invoiceResponse.data.due_date).toISOString().split('T')[0]
          : null,
      });

      setViewInvoiceItems(invoiceItemsResponse.data);
      setOpenViewInvoiceModal(true);

    } catch (error) {
      console.error("Error fetching invoice details:", error);
      enqueueSnackbar("Failed to load invoice details", { variant: "error" });
    } finally {
      setIsViewLoading(false);
      setViewLoadingId(null);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    if (isDownloading) {
      console.warn("Download already in progress");
      return;
    }

    setIsDownloading(true);
    setDownloadLoadingId(invoiceId);

    try {
      // Fetch the templates to check the default template
      const templatesResponse = await axiosInstance.get('/templates/');
      const defaultTemplate = templatesResponse.data.find(template => template.is_default && template.type === 'invoice');

      // Check if the default template is "Standard India Template"
      const isIndiaTemplate = defaultTemplate && defaultTemplate.name === 'Standard India Template';
      console.log(`Using ${isIndiaTemplate ? 'India' : 'Global'} template format for invoice`);

      // Pass this information when generating the PDF
      const response = await axiosInstance.get(`/invoices/pdf/${invoiceId}`, {
        responseType: 'blob',
        params: { isIndiaTemplate }, // Pass the flag as a query parameter
        timeout: 30000 // 30 second timeout
      });

      // Check if response is valid blob (PDF)
      if (response.data.type !== 'application/pdf') {
        const reader = new FileReader();
        reader.onload = function () {
          try {
            // Try to parse as JSON in case it contains error info
            const responseData = JSON.parse(reader.result);
            throw new Error(responseData.detail || "Invalid response format");
          } catch (parseError) {
            throw new Error("Server returned invalid data format");
          }
        };
        reader.readAsText(response.data);
        return;
      }

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;

      // Prefer business invoice_number (matches PDF API); fall back to id only if missing.
      const invoiceRecord = invoices.find((inv) => inv.id === invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Invoice_${invoiceRecord?.invoice_number || invoiceId}.pdf`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          filename = fileNameMatch[1].replace(/["']/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);

      // Trigger download
      link.click();

      // Clean up
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);

      // --- Log PDF download ---
      await frontendLogger.logCreate('Invoice', {
        id: invoiceId,
        action: 'pdf_download',
        user: user?.id,
        timestamp: new Date().toISOString(),
      });

      enqueueSnackbar("Invoice downloaded successfully", { variant: "success" });
    } catch (error) {
      console.error("Error downloading invoice:", error);

      // Check if there's a specific error message from the server
      let errorMessage = "Failed to download invoice";

      // Handle network errors specifically
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timed out. The server is taking too long to respond.";
      } else if (error.message && error.message.includes('Network Error')) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (error.response) {
        // If the server responded with an error
        if (error.response.data) {
          try {
            // Try to extract error message from response data
            if (error.response.data instanceof Blob) {
              const reader = new FileReader();

              // Create a promise to handle the async blob reading
              await new Promise((resolve, reject) => {
                reader.onload = () => {
                  try {
                    const result = JSON.parse(reader.result);
                    if (result && result.detail) {
                      errorMessage = result.detail;
                    }
                    resolve();
                  } catch (e) {
                    // If it's not parseable JSON, just use the text
                    if (typeof reader.result === 'string' && reader.result.trim().length > 0) {
                      errorMessage = reader.result;
                    }
                    resolve();
                  }
                };
                reader.onerror = reject;
                reader.readAsText(error.response.data);
              });
            } else if (error.response.data.detail) {
              errorMessage = error.response.data.detail;
            } else if (typeof error.response.data === 'string') {
              // If response data is a string, use it as message
              errorMessage = error.response.data;
            }
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
          }
        }

        // Add status code if available
        if (error.response.status) {
          errorMessage += ` (Status: ${error.response.status})`;
        }
      } else if (error.message) {
        // Use the error message directly if available
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsDownloading(false);
      setDownloadLoadingId(null);
    }
  };

  // Add near the top with other constants
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];
  // Add these functions after your existing functions
  const handleToggleEmailModal = async () => {
    if (!isEmailModalOpen) {
      setIsEmailLoading(true);
      try {
        const emailConfigResponse = await axiosInstance.get('/email-settings');
        if (!emailConfigResponse.data?.email_provider) {
          throw new Error('Email is not configured. Please configure email settings first.');
        }
        if (!viewInvoice || !viewInvoice.account_id) {
          throw new Error('Invoice data is incomplete');
        }

        // Get account data
        const accountResponse = await axiosInstance.get(`/accounts/${viewInvoice.account_id}`);
        const accountEmail = accountResponse.data.email || '';

        // Generate PDF blob
        const pdfResponse = await axiosInstance.get(`/invoices/pdf/${viewInvoice.id}`, {
          responseType: 'blob'
        });

        const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });

        // Check file size
        const maxSize = 25 * 1024 * 1024; // 25MB
        if (pdfBlob.size > maxSize) {
          throw new Error('PDF file exceeds 25MB limit');
        }

        // Set email attachments — filename must match the business invoice number
        // (same as PDF API Content-Disposition), not the internal DB id.
        setEmailAttachments([{
          filename: `Invoice_${viewInvoice.invoice_number || viewInvoice.id}.pdf`,
          size: pdfBlob.size,
          content_type: 'application/pdf',
          blob: pdfBlob,
        }]);

        // Set initial email fields
        setNewEmail({
          to: accountEmail,
          cc: '',
          bcc: '',
          subject: `Invoice ${viewInvoice.invoice_number}`,
          content: `Please find attached Invoice ${viewInvoice.invoice_number}`,
        });

      } catch (error) {
        console.error('Error preparing email:', error);
        const message =
          error?.message?.includes('not configured')
            ? error.message
            : error?.response?.status === 404
              ? 'Email is not configured. Please configure email settings first.'
              : `Error preparing email: ${error.message}`;
        setSnackbar({
          open: true,
          message,
          severity: 'error'
        });
        return; // Don't open modal if there's an error
      } finally {
        setIsEmailLoading(false);
      }
    } else {
      setEmailAttachments([]); // Clear attachments when closing
    }

    setIsEmailModalOpen(!isEmailModalOpen);
  };

  const handleSendEmail = async () => {
    setIsEmailSending(true);
    try {
      // Check if email service is configured
      const emailConfigResponse = await axiosInstance.get('/email-settings');
      if (!emailConfigResponse.data?.email_provider) {
        throw new Error('Email is not configured. Please configure email settings first.');
      }

      const attachments = await serializeEmailAttachments(emailAttachments);

      // Create the email payload
      const emailPayload = {
        to: newEmail.to,
        cc: newEmail.cc || '',
        bcc: newEmail.bcc || '',
        subject: newEmail.subject,
        content: newEmail.content,
        attachments: attachments.length > 0 ? attachments : undefined,
        metadata: {
          parent_type: 'invoice',
          parent_id: viewInvoice.id
        }
      };

      // Send email
      await axiosInstance.post('/emails/send', emailPayload);

      // Close modal and show success message
      setIsEmailModalOpen(false);
      setEmailAttachments([]);
      setSnackbar({
        open: true,
        message: 'Email sent successfully!',
        severity: 'success',
      });

    } catch (error) {
      console.error('Error sending email:', error);

      // Handle different types of errors
      let errorMessage = 'Failed to send email';

      if (error.message.includes('not configured')) {
        errorMessage = error.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Email is not configured. Please configure email settings first.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsEmailSending(false);
    }
  };

  const handleQuillChange = (content) => {
    setNewEmail({ ...newEmail, content: content });
  };

  const toggleCc = () => setShowCc(!showCc);
  const toggleBcc = () => setShowBcc(!showBcc);

  const fetchInvoiceHtml = async (invoiceId) => {
    try {
      // Add query parameter to request all items without pagination
      const response = await axiosInstance.get(`/invoices/html/${invoiceId}?all_items=true&include_pagination=false`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice HTML:', error);
      return null;
    }
  };

  const formatIndianNumber = (number) => {
    if (isNaN(number) || number === null || number === undefined) return "0.00";

    const [integerPart, decimalPart] = Number(number).toFixed(2).split(".");
    const lastThreeDigits = integerPart.slice(-3);
    const otherDigits = integerPart.slice(0, -3);

    let formattedNumber = "";
    if (otherDigits) {
      formattedNumber = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThreeDigits;
    } else {
      formattedNumber = lastThreeDigits;
    }

    return `${formattedNumber}.${decimalPart}`;
  };

  const formatCurrencyByAccount = (number, accountCurrency) => {
    if (accountCurrency === 'INR') {
      return '₹ ' + formatIndianNumber(number);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: accountCurrency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(number);
    }
  };

  const columns = [
    {
      Header: "Invoice Number",
      accessor: "invoice_number",
      width: "10%",
      Cell: ({ row }) => (
        <MDTypography
          component="span"
          variant="button"
          color="info"
          fontWeight="medium"
          sx={{ cursor: 'pointer' }}
          onClick={e => {
            e.stopPropagation();
            navigate(`/invoices/${row.original.id}`);
          }}
        >
          {row.original.invoice_number}
        </MDTypography>
      )
    },
    {
      Header: "Total Amount",
      accessor: "total_amount",
      width: "10%",
      Cell: ({ row }) => formatCurrencyByAccount(
        row.original.total_amount,
        // Invoice currency is source of truth; account currency is only a fallback.
        row.original.currency || row.original.account_currency || 'INR'
      )
    },
    {
      Header: "Order Number",
      accessor: "order_number",
      width: "10%",
      Cell: ({ row }) => {
        const { order_number, sales_order_id, travel_order_id } = row.original;
        const label =
          order_number ||
          (travel_order_id ? `Travel Order #${travel_order_id}` : null);
        if (!label) return '-';
        const href = travel_order_id
          ? `/travel-order/${travel_order_id}`
          : sales_order_id
            ? `/orders/${sales_order_id}`
            : null;
        if (!href) return label;
        return (
          <MDTypography
            component="span"
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(href);
            }}
          >
            {label}
          </MDTypography>
        );
      }
    },
    {
      Header: "Date",
      accessor: "invoice_date",
      width: "10%",
      Cell: ({ value }) => (value ? formatDate(value, 'date') : "-"),
    },
    { Header: "Due Date", accessor: "due_date", width: "10%", Cell: ({ value }) => (value ? formatDate(value, 'date') : "-") },
    { Header: "Status", accessor: "status", width: "10%" },
    {
      Header: "Modified Date",
      accessor: "updated_at",
      width: "15%",
      Cell: ({ value }) => (
        <span>
          {value ? formatDate(value) : "-"}
        </span>
      ),
    }
    // {
    //   Header: "Actions",
    //   accessor: "actions",
    //   width: "10%",
    //   Cell: ({ row }) => (
    //     <MDBox>
    //       {/* <Tooltip title="Edit Invoice">
    //         <span>
    //           <IconButton
    //             onClick={() => handleEditInvoice(row.original.id)}
    //             disabled={editLoadingId === row.original.id}
    //           >
    //             {editLoadingId === row.original.id ? (
    //               <CircularProgress size={24} />
    //             ) : (
    //               <Icon>edit</Icon>
    //             )}
    //           </IconButton>
    //         </span>
    //       </Tooltip>

    //       <Tooltip title="View Invoice">
    //         <span>
    //           <IconButton
    //             onClick={() => displayInvoice(row.original.id)}
    //             disabled={viewLoadingId === row.original.id}
    //           >
    //             {viewLoadingId === row.original.id ? (
    //               <CircularProgress size={24} />
    //             ) : (
    //               <Icon>visibility</Icon>
    //             )}
    //           </IconButton>
    //         </span>
    //       </Tooltip>

    //       <Tooltip title="Download PDF Invoice">
    //         <span>
    //           <IconButton
    //             onClick={() => downloadInvoice(row.original.id)}
    //             disabled={downloadLoadingId === row.original.id}
    //           >
    //             {downloadLoadingId === row.original.id ? (
    //               <CircularProgress size={24} />
    //             ) : (
    //               <Icon>download</Icon>
    //             )}
    //           </IconButton>
    //         </span>
    //       </Tooltip> */}

    //       <Tooltip title="Generate GST E-Invoice">
    //         <span>
    //           <IconButton
    //             onClick={() => handleGenerateGSTEinvoice(row.original.id)}
    //           >
    //             <Icon>receipt_long</Icon>
    //           </IconButton>
    //         </span>
    //       </Tooltip>
    //     </MDBox>
    //   )
    // }
  ];

  const calculateTotalAmount = (items = editedInvoiceItems) => {
    let subtotal = 0;
    let taxSummary = {};

    // Use passed items or default to editedInvoiceItems
    const itemsToCalculate = items || editedInvoiceItems;

    itemsToCalculate.forEach((item) => {
      if (item.markedForDeletion) return;

      const unitPrice = parseFloat(item.unit_price) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discountPercentage = parseFloat(item.discount_percentage) || 0;
      const taxRate = parseFloat(item.tax) || 0;

      const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
      const totalDiscountedPrice = discountedPrice * quantity;

      subtotal += totalDiscountedPrice;

      // Tax on discounted net (after discount).
      const taxAmount = totalDiscountedPrice * (taxRate / 100);

      if (taxRate) {
        taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
      }

      item.discounted_price = discountedPrice;
      item.total_discounted_price = totalDiscountedPrice;
      item.tax_amount = taxAmount;
      item.total_price = totalDiscountedPrice + taxAmount;
    });

    const totalTax = Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);
    const grandTotal = subtotal + totalTax;

    return { subtotal, grandTotal, taxSummary };
  };

  const handleGenerateGSTEinvoice = async (invoiceId) => {
    try {
      const response = await axiosInstance.post(`/invoices/${invoiceId}/gst-einvoice/`);
      console.log("GST E-Invoice Response:", response.data);
      // --- Log GST generation ---
      await frontendLogger.logUpdate('Invoice', {
        id: invoiceId,
        action: 'gst_generate',
        user: user?.id,
        timestamp: new Date().toISOString(),
      });
      enqueueSnackbar("GST E-Invoice generated! Check console for response.", { variant: "success" });
    } catch (error) {
      console.error("Error generating GST E-Invoice:", error);
      enqueueSnackbar("Failed to generate GST E-Invoice", { variant: "error" });
    }
  };

  if (isLoading || travelUiLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          title="Loading Invoice Data"
          message="Please wait while we fetch the Invoices information..."
        />
        
      </DashboardLayout>
    );
  }

  const handleRemoveOrderItem = (index) => {
    const updatedItems = editedInvoiceItems.filter((_, i) => i !== index);
    setEditedInvoiceItems(updatedItems);
  };

  const isEditable = editedInvoice?.status === 'Draft';

  // Shared by the classic and v2 layouts below: the same actions and table.
  const listActions = (
    <>
      {showTravel && canCreate && (
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleOpenCreateInvoiceModal}
          disabled={isCreatingManualInvoice}
        >
          {isCreatingManualInvoice ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <>
              <Icon sx={{ fontWeight: "bold", mr: 1 }}>add</Icon>
              Create Invoice
            </>
          )}
        </MDButton>
      )}
    </>
  );
  const listBody = (
    <>
      {
        filteredInvoices.length > 0 ? (
          <DataTable
            table={{ columns, rows: invoices }}
            isSorted={true}
            isLoading={isLoading}
            onRowClick={handleEditInvoice}
            searchPlaceholder="Search for invoices..."
            noEndBorder
            canSearch
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            showTotalEntries
            pagination={{ variant: "gradient", color: "info" }}
            exportCSV={true}
            objectType="invoice"
            onFilterApply={(conditions) => {
              console.log('Filter applied:', conditions);
            }}
            onFilterClear={() => {
              console.log('Filter cleared');
              fetchInvoices();
            }}
          />
        ) : (
          <EmptyState
            moduleName="Invoice"
            // filterType={casesFilter}
            canCreate={canCreate}
          />
        )}
    </>
  );

  const v2Main = (
    <MDBox>
      <PageHeader title={invoicesLabel} summary={`${invoices.length} ${invoices.length === 1 ? "invoice" : invoicesLabel.toLowerCase()}`} actions={listActions} />
      <Card>
        <MDBox pt={1}>{listBody}</MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" color="white">
                    Invoices
                  </MDTypography>
                  {listActions}
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                {listBody}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      )}
      

      <Dialog
        open={isDownloading}
        onClose={() => {
          // Only allow closing if not in the middle of a download
          if (!downloadLoadingId) {
            setIsDownloading(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <div className="flex flex-col items-center justify-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <h2 className="text-lg font-semibold mb-2">Generating Invoice</h2>
            <p className="text-center text-sm text-muted-foreground mb-4">
              Please wait while we prepare your invoice for download. This may take a few moments.
            </p>
            <MDButton
              variant="text"
              color="secondary"
              onClick={() => {
                setIsDownloading(false);
                setDownloadLoadingId(null);
                enqueueSnackbar("Download canceled", { variant: "info" });
              }}
            >
              Cancel
            </MDButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openCreateInvoiceModal}
        onClose={() => !isCreatingManualInvoice && setOpenCreateInvoiceModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 3,
            py: 2.5,
          }}
        >
          <MDTypography variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
            Create Manual Invoice
          </MDTypography>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={travelOrders}
                getOptionLabel={(option) => option?.name || option?.order_id || `Order ${option?.id}`}
                value={travelOrders.find((o) => String(o.id) === String(manualInvoiceForm.travel_order_id)) || null}
                onChange={(e, val) => handleTravelOrderSelect(val?.id || '')}
                loading={loadingOrderDetail}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Travel Order *"
                    margin="normal"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOrderDetail ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={invoiceTypeOptions}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.key === value?.key}
                value={invoiceTypeOptions.find((o) => o.key === (
                  manualInvoiceForm.invoice_type === 'installment'
                    ? `installment-${manualInvoiceForm.installment_number}`
                    : manualInvoiceForm.invoice_type
                )) || null}
                onChange={(e, val) => handleInvoiceTypeSelect(val)}
                disabled={!manualInvoiceForm.travel_order_id || loadingOrderDetail}
                noOptionsText={
                  manualInvoiceForm.travel_order_id
                    ? (loadingOrderDetail ? 'Loading payment terms...' : 'No invoice types available for this order')
                    : 'Select a travel order first'
                }
                renderInput={(params) => (
                  <TextField {...params} label="Invoice Type *" margin="normal" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Invoice Amount *"
                type="number"
                fullWidth
                margin="normal"
                value={manualInvoiceForm.total_amount}
                onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, total_amount: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Due Date"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={manualInvoiceForm.due_date}
                onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, due_date: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Terms & Conditions"
                fullWidth
                margin="normal"
                multiline
                minRows={2}
                value={manualInvoiceForm.notes}
                onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
          <MDBox mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <MDButton
              color="secondary"
              onClick={() => setOpenCreateInvoiceModal(false)}
              disabled={isCreatingManualInvoice}
            >
              Cancel
            </MDButton>
            <MDButton
              color="info"
              onClick={handleCreateManualInvoice}
              disabled={isCreatingManualInvoice || !manualInvoiceForm.invoice_type}
            >
              {isCreatingManualInvoice ? <CircularProgress size={20} color="inherit" /> : 'Create'}
            </MDButton>
          </MDBox>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openEditInvoiceModal}
        onClose={() => setOpenEditInvoiceModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            width: '1200px',
            maxWidth: '100%'
          }
        }}
      >
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 3,
            py: 2.5,
          }}
        >
          <MDTypography
            variant="h6"
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
            Edit Invoice
          </MDTypography>
        </Box>

        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => option?.label || ''}
                value={accounts.find(acc => acc.value === editedInvoice?.account_id) || null}
                onChange={(e, newValue) => {
                  handleInvoiceFieldChange('account_id', newValue?.value);
                  handleInvoiceFieldChange('account_name', newValue?.label);
                  handleInvoiceFieldChange('account_currency', newValue?.currency);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account"
                    fullWidth
                    margin="normal"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px"
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Invoice Date"
                type="date"
                value={editedInvoice?.invoice_date || ''}
                onChange={(e) => handleInvoiceFieldChange('invoice_date', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Due Date"
                type="date"
                value={editedInvoice?.due_date || ''}
                onChange={(e) => handleInvoiceFieldChange('due_date', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
                inputProps={{
                  min: editedInvoice?.invoice_date || undefined,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={
                  Boolean(
                    editedInvoice?.invoice_date &&
                    editedInvoice?.due_date &&
                    editedInvoice.due_date < editedInvoice.invoice_date
                  )
                }
                helperText={
                  editedInvoice?.invoice_date &&
                  editedInvoice?.due_date &&
                  editedInvoice.due_date < editedInvoice.invoice_date
                    ? 'Due date cannot be earlier than invoice date'
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={['Draft', 'Sent', 'Completed', 'Cancelled']}
                value={editedInvoice?.status || ''}
                onChange={(e, newValue) => handleInvoiceFieldChange('status', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Invoice Status"
                    fullWidth
                    margin="normal"
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                    disabled={!isEditable}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Notes"
                value={editedInvoice?.notes || ''}
                onChange={(e) => handleInvoiceFieldChange('notes', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
              />
            </Grid>
          </Grid>

          <MDBox
            sx={{
              mt: 3,
              mb: 3,
              border: "1px solid #dee2e6",
              borderRadius: "0.75rem",
              overflow: "auto",
              maxHeight: "400px"
            }}
          >
            {/* Header */}
            <MDBox
              sx={{
                display: "flex",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #dee2e6",
                position: "sticky",
                top: 0,
                zIndex: 1
              }}
            >
              <MDBox width="25%" p={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Product Name
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Unit Price
                </MDTypography>
              </MDBox>
              <MDBox width="8%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Tax %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discount %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discounted Price
                </MDTypography>
              </MDBox>
              <MDBox width="10%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Quantity
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Total Net Price
                </MDTypography>
              </MDBox>
              <MDBox width="9%" p={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Actions
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Body */}
            <MDBox>
              {editedInvoiceItems
                .filter(item => !item.markedForDeletion)
                .map((item, index) => {
                  const discountedPrice = (item.unit_price || 0) - ((item.unit_price || 0) * (item.discount_percentage || 0) / 100);
                  const totalNetPrice = discountedPrice * (item.quantity || 0);
                  return (
                    <MDBox
                      key={item.tempId}
                      display="flex"
                      sx={{
                        borderBottom: "1px solid #dee2e6",
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}
                    >
                      <MDBox width="25%" p={2}>
                        <Autocomplete
                          options={productList}
                          getOptionLabel={(option) => option.name}
                          value={productList.find((product) => product.name === item.product_name) || null}
                          onChange={(e, newValue) =>
                            handleInvoiceItemChange(item.tempId, 'product_name', newValue ? newValue.name : '')
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Product Name"
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': { borderColor: '#dee2e6' },
                                  '&:hover fieldset': { borderColor: '#7b809a' }
                                }
                              }}
                            />
                          )}
                          disabled={!isEditable}
                        />
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrencyByAccount(item.unit_price || 0, editedInvoice?.currency || editedInvoice?.account_currency)}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="8%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {item.tax}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <TextField
                          value={item.discount_percentage}
                          onChange={(e) =>
                            handleInvoiceItemChange(
                              item.tempId,
                              'discount_percentage',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          type="number"
                          disabled={!isEditable}
                          size="small"
                          sx={{
                            width: '70px',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#dee2e6' },
                              '&:hover fieldset': { borderColor: '#7b809a' }
                            }
                          }}
                        />
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrencyByAccount(discountedPrice, editedInvoice?.currency || editedInvoice?.account_currency)}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="10%" p={2} textAlign="right">
                        <TextField
                          value={item.quantity}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              handleInvoiceItemChange(item.tempId, 'quantity', '');
                              return;
                            }
                            const parsed = parseInt(raw, 10);
                            handleInvoiceItemChange(
                              item.tempId,
                              'quantity',
                              Number.isNaN(parsed) ? '' : parsed
                            );
                          }}
                          type="number"
                          size="small"
                          disabled={!isEditable}
                          inputProps={{ min: 1, step: 1 }}
                          error={item.quantity !== '' && Number(item.quantity) <= 0}
                          sx={{
                            width: '70px',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#dee2e6' },
                              '&:hover fieldset': { borderColor: '#7b809a' }
                            }
                          }}
                        />
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrencyByAccount(totalNetPrice, editedInvoice?.currency || editedInvoice?.account_currency)}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="9%" p={2} display="flex" alignItems="center">
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveInvoiceItem(item.tempId)}
                          disabled={!isEditable || editedInvoiceItems.filter(i => !i.markedForDeletion).length <= 1}
                          size="small"
                        >
                          <RemoveIcon />
                        </IconButton>
                        <IconButton
                          color="info"
                          onClick={handleAddInvoiceItem}
                          disabled={!isEditable}
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </MDBox>
                    </MDBox>
                  );
                })}
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              mt: 3,
              p: 2,
              borderTop: '2px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <MDBox sx={{ minWidth: '300px' }}>
              {(() => {
                const { subtotal, grandTotal, taxSummary } = calculateTotalAmount();
                const currency = editedInvoice?.currency || editedInvoice?.account_currency || 'INR';

                return (
                  <MDBox>
                    <MDBox display="flex" justifyContent="space-between" mb={1}>
                      <MDTypography variant="button" color="text" fontWeight="regular">
                        Subtotal:
                      </MDTypography>
                      <MDTypography variant="button" color="text" fontWeight="medium">
                        {formatCurrencyByAccount(subtotal, currency)}
                      </MDTypography>
                    </MDBox>

                    {Object.entries(taxSummary).map(([taxRate, taxAmount]) => (
                      <MDBox key={taxRate} display="flex" justifyContent="space-between" mb={1}>
                        <MDTypography variant="button" color="text" fontWeight="regular">
                          Tax ({taxRate}%):
                        </MDTypography>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          {formatCurrencyByAccount(taxAmount, currency)}
                        </MDTypography>
                      </MDBox>
                    ))}

                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      mt={2}
                      pt={1.5}
                      sx={{ borderTop: '1px solid #dee2e6' }}
                    >
                      <MDTypography variant="button" color="dark" fontWeight="medium">
                        Total Amount:
                      </MDTypography>
                      <MDTypography variant="button" color="info" fontWeight="bold">
                        {formatCurrencyByAccount(grandTotal, currency)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })()}
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton
            onClick={() => setOpenEditInvoiceModal(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveInvoice}
            variant="gradient"
            color="info"
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      <InvoiceViewer
        open={openViewInvoiceModal}
        onClose={() => setOpenViewInvoiceModal(false)}
        invoice={viewInvoice}
        invoiceItems={viewInvoiceItems}
        onSendEmail={handleToggleEmailModal}
        isEmailLoading={isEmailLoading}
        formatCurrencyByAccount={formatCurrencyByAccount}
        colors={colors}
      />

      <EmailModal
        open={isEmailModalOpen}
        onClose={handleToggleEmailModal}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        handleSend={handleSendEmail}
        isGenerating={isGenerating}
        errorMessage={errorMessage}
        emailContext={emailContext}
        setEmailContext={setEmailContext}
        showCc={showCc}
        showBcc={showBcc}
        toggleCc={toggleCc}
        toggleBcc={toggleBcc}
        handleQuillChange={handleQuillChange}
        modules={modules}
        formats={formats}
        loading={isEmailLoading}
        attachments={emailAttachments}
        currentAttachment={currentAttachment}
        setEmailAttachments={setEmailAttachments} // Add this line
        isSending={isEmailSending} // Add this line too for better loading state handling
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );

}

export default Invoices;